import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { RouteAnnouncer } from "./components/RouteAnnouncer";

// Pages
import Login from "./pages/Login";
import Register from "./pages/Register";
import Dashboard from "./pages/Dashboard";
import Projects from "./pages/Projects";
import ProjectDetail from "./pages/ProjectDetail";
import ProjectForm from "./pages/ProjectForm";
import Masters from "./pages/Masters";
import MasterDetail from "./pages/MasterDetail";
import MasterForm from "./pages/MasterForm";
import Movements from "./pages/Movements";
import MovementDetail from "./pages/MovementDetail";
import MovementForm from "./pages/MovementForm";
import Users from "./pages/Users";
import LabelPrint from "./pages/LabelPrint";
import CompanySelector from "./pages/CompanySelector";
import ImportData from "./pages/ImportData";
import StockTable from "./pages/StockTable";

// Protected Route Component
const ProtectedRoute = ({ children, adminOnly = false, superAdminOnly = false }) => {
    const { user, loading, isSuperAdmin } = useAuth();

    if (loading) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center">
                <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    if (!user) {
        return <Navigate to="/login" replace />;
    }

    if (superAdminOnly && !isSuperAdmin()) {
        return <Navigate to="/dashboard" replace />;
    }

    if (adminOnly && user.role !== 'admin' && user.role !== 'superadmin') {
        return <Navigate to="/dashboard" replace />;
    }

    return children;
};

// Public Route Component (redirects to dashboard if logged in)
const PublicRoute = ({ children }) => {
    const { user, loading } = useAuth();

    if (loading) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center">
                <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    if (user) {
        return <Navigate to="/dashboard" replace />;
    }

    return children;
};

function AppRoutes() {
    return (
        <Routes>
            {/* Public Routes */}
            <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
            <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />

            {/* Protected Routes */}
            <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
            
            {/* Projects */}
            <Route path="/projects" element={<ProtectedRoute><Projects /></ProtectedRoute>} />
            <Route path="/projects/new" element={<ProtectedRoute><ProjectForm /></ProtectedRoute>} />
            <Route path="/projects/:id" element={<ProtectedRoute><ProjectDetail /></ProtectedRoute>} />
            <Route path="/projects/:id/edit" element={<ProtectedRoute><ProjectForm /></ProtectedRoute>} />
            <Route path="/projects/:projectId/masters/new" element={<ProtectedRoute><MasterForm /></ProtectedRoute>} />

            {/* Masters */}
            <Route path="/masters" element={<ProtectedRoute><Masters /></ProtectedRoute>} />
            <Route path="/masters/:id" element={<ProtectedRoute><MasterDetail /></ProtectedRoute>} />
            <Route path="/masters/:id/edit" element={<ProtectedRoute><MasterForm /></ProtectedRoute>} />
            <Route path="/masters/:id/label" element={<ProtectedRoute><LabelPrint /></ProtectedRoute>} />

            {/* Movements */}
            <Route path="/movements" element={<ProtectedRoute><Movements /></ProtectedRoute>} />
            <Route path="/movements/new" element={<ProtectedRoute><MovementForm /></ProtectedRoute>} />
            <Route path="/movements/:id" element={<ProtectedRoute><MovementDetail /></ProtectedRoute>} />

            {/* Stock Table */}
            <Route path="/stock" element={<ProtectedRoute><StockTable /></ProtectedRoute>} />

            {/* Users (Admin only) */}
            <Route path="/users" element={<ProtectedRoute adminOnly><Users /></ProtectedRoute>} />

            {/* Companies (SuperAdmin only) */}
            <Route path="/companies" element={<ProtectedRoute superAdminOnly><CompanySelector /></ProtectedRoute>} />
            <Route path="/import" element={<ProtectedRoute superAdminOnly><ImportData /></ProtectedRoute>} />

            {/* Default redirect */}
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
    );
}

function App() {
    return (
        <BrowserRouter>
            <ThemeProvider>
            <AuthProvider>
                <AppRoutes />
                <RouteAnnouncer />
                <Toaster 
                    position="top-right" 
                    richColors 
                    closeButton
                    toastOptions={{
                        style: {
                            background: 'hsl(var(--card))',
                            border: '1px solid hsl(var(--border))',
                            color: 'hsl(var(--foreground))'
                        }
                    }}
                />
            </AuthProvider>
            </ThemeProvider>
        </BrowserRouter>
    );
}

export default App;
